import { DateAsString, ForsikringDekningDTO, SporsmalType } from '../../types/generated-types';
import { Kundeinfo } from '../../types/internal-types';
import { forsikringSporsmalFinnerIkkeRiktigAlternativ } from '../forsikring/forsikring-types';
import { filterForsikringerBySkadeDatoAndPrioritization } from '../forsikring/forsikring-utils';
import { CurrentSporsmal } from '../reducer/current-steg';
import { ForsikringAlternativ, SkadedatoAndForsikringSporsmal } from '../reducer/sporsmalsflyt-types';

export enum ForsikringDataType {
    Ubesvart = 'Ubesvart',
    Forsikringsliste = 'Forsikringsliste',
    ForsikringIngenAlternativer = 'ForsikringIngenAlternativer',
}

export interface ForsikringListeData {
    type: ForsikringDataType.Forsikringsliste;
    alternativer: ForsikringAlternativ[];
    selectedAlternativ?: ForsikringAlternativ;
}

export interface ForsikringIngenAlternativerData {
    type: ForsikringDataType.ForsikringIngenAlternativer;
    id: ForsikringDekningDTO;
}

export interface UbesvartData {
    type: ForsikringDataType.Ubesvart;
}

export type ForsikringData = ForsikringListeData | ForsikringIngenAlternativerData | UbesvartData;
export type ForsikringDataBesvart = ForsikringListeData | ForsikringIngenAlternativerData;

export interface CurrentSkadedatoAndForsikringSporsmal {
    type: SporsmalType.SkadedatoAndForsikring;
    sporsmal: SkadedatoAndForsikringSporsmal;
    forsikringData: ForsikringData;
    skadedato?: DateAsString;
}

export interface BesvartSkadedatoAndForsikringSporsmal {
    type: SporsmalType.SkadedatoAndForsikring;
    sporsmal: SkadedatoAndForsikringSporsmal;
    forsikringData: ForsikringDataBesvart;
    skadedato: DateAsString;
}

export const isSkadedatoAndForsikringSporsmal = (
    currentSporsmal: CurrentSporsmal,
): currentSporsmal is CurrentSkadedatoAndForsikringSporsmal =>
    currentSporsmal.type === SporsmalType.SkadedatoAndForsikring;

const isSkadedatoBesvartInSkadedatoAndForsikringSporsmal = (
    currentSporsmal: CurrentSkadedatoAndForsikringSporsmal,
): boolean => !!currentSporsmal.skadedato;

export const isForsikringListeType = (data: ForsikringData): data is ForsikringListeData =>
    data.type === ForsikringDataType.Forsikringsliste;

export const isForsikringIngenAlternativerType = (data: ForsikringData): data is ForsikringIngenAlternativerData =>
    data.type === ForsikringDataType.ForsikringIngenAlternativer;

const isForsikringListeBesvartInSkadedatoAndForsikringSporsmal = (
    currentSporsmal: CurrentSkadedatoAndForsikringSporsmal,
): boolean =>
    isForsikringListeType(currentSporsmal.forsikringData) && !!currentSporsmal.forsikringData.selectedAlternativ;

export const isBesvartSkadedatoAndForsikringSporsmal = (
    currentSporsmal: CurrentSkadedatoAndForsikringSporsmal,
): currentSporsmal is BesvartSkadedatoAndForsikringSporsmal =>
    (isForsikringListeBesvartInSkadedatoAndForsikringSporsmal ||
        isForsikringIngenAlternativerType(currentSporsmal.forsikringData)) &&
    isSkadedatoBesvartInSkadedatoAndForsikringSporsmal(currentSporsmal) &&
    isSkadedatoAndForsikringSporsmal(currentSporsmal);

export const toCurrentSkadedatoAndForsikringSporsmal = (
    sporsmal: SkadedatoAndForsikringSporsmal,
    kundeinfo: Kundeinfo,
    skadedato?: DateAsString,
): CurrentSkadedatoAndForsikringSporsmal => {
    if (skadedato === undefined) {
        return {
            type: SporsmalType.SkadedatoAndForsikring,
            sporsmal,
            forsikringData: {
                type: ForsikringDataType.Ubesvart,
            },
            skadedato,
        };
    }
    const forsikringer = kundeinfo?.forsikringer || [];
    const forsikringsalternativer: ForsikringAlternativ[] = filterForsikringerBySkadeDatoAndPrioritization(
        forsikringer,
        new Date(skadedato),
        sporsmal.forsikringsprodukterPriority,
    ).map((forsikring) => ({
        id: forsikring.dekning,
        tekst: forsikring.visningstekst,
        forsikringsnummer: forsikring.forsikringsnummer,
        forsikringversjon: forsikring.versjon,
        forsikringsperiode: forsikring.periode,
        forsikringsprodukt: forsikring.produkt,
    }));

    if (forsikringsalternativer.length === 0) {
        return {
            type: SporsmalType.SkadedatoAndForsikring,
            sporsmal,
            forsikringData: {
                type: ForsikringDataType.ForsikringIngenAlternativer,
                id: ForsikringDekningDTO.IngenValgt,
            },
            skadedato,
        };
    }

    return {
        type: SporsmalType.SkadedatoAndForsikring,
        sporsmal,
        forsikringData: {
            type: ForsikringDataType.Forsikringsliste,
            alternativer: [
                ...forsikringsalternativer,
                forsikringSporsmalFinnerIkkeRiktigAlternativ(sporsmal.finnerIkkeRiktigForsikringAlternativTekst),
            ],
        },
        skadedato,
    };
};

export const updateCurrentSkadedatoAndForsikringSporsmalWithSkadedato = (
    currentSporsmal: CurrentSporsmal,
    kundeinfo: Kundeinfo,
    skadedato?: DateAsString,
): CurrentSporsmal => {
    if (currentSporsmal.type === SporsmalType.SkadedatoAndForsikring) {
        const forsikringer = kundeinfo?.forsikringer || [];
        const forsikringsalternativer: ForsikringAlternativ[] = filterForsikringerBySkadeDatoAndPrioritization(
            forsikringer,
            new Date(skadedato),
            currentSporsmal.sporsmal.forsikringsprodukterPriority,
        ).map((forsikring) => ({
            id: forsikring.dekning,
            tekst: forsikring.visningstekst,
            forsikringsnummer: forsikring.forsikringsnummer,
            forsikringversjon: forsikring.versjon,
            forsikringsperiode: forsikring.periode,
            forsikringsprodukt: forsikring.produkt,
        }));

        if (forsikringsalternativer.length === 0) {
            return {
                ...currentSporsmal,
                skadedato: skadedato || undefined,
                forsikringData: {
                    type: ForsikringDataType.ForsikringIngenAlternativer,
                    id: ForsikringDekningDTO.IngenValgt,
                },
            };
        }

        return {
            ...currentSporsmal,
            skadedato: skadedato || undefined,
            forsikringData: {
                ...currentSporsmal.forsikringData,
                type: ForsikringDataType.Forsikringsliste,
                alternativer: [
                    ...forsikringsalternativer,
                    forsikringSporsmalFinnerIkkeRiktigAlternativ(
                        currentSporsmal.sporsmal.finnerIkkeRiktigForsikringAlternativTekst,
                    ),
                ],
            },
        };
    }
    return currentSporsmal;
};

export const updateCurrentSkadedatoAndForsikringSporsmalWithSelectedAlternativ = (
    currentSporsmal: CurrentSporsmal,
    selectedAlternativ: ForsikringAlternativ,
): CurrentSporsmal => {
    if (
        currentSporsmal.type === SporsmalType.SkadedatoAndForsikring &&
        isForsikringListeType(currentSporsmal.forsikringData)
    ) {
        return {
            ...currentSporsmal,
            forsikringData: {
                ...currentSporsmal.forsikringData,
                selectedAlternativ,
            },
        };
    }
    return currentSporsmal;
};
